import { useEffect, useState, useMemo } from "react";
import {
  Rocket, Satellite, Cpu, Bot, CircuitBoard, Radar, Wifi, Atom,
  Zap, Cog, Telescope, Plane, Wrench, Boxes, Hexagon, Binary,
  Network, Antenna, RadioTower, Beaker, FlaskConical, Activity,
  Waves, Sigma, Orbit, Microscope, Cable, Cctv, HardDrive,
  Joystick, Lightbulb, MemoryStick, MonitorSmartphone, MousePointer,
  Plug, Power, Printer, ScanLine, Smartphone, Tablet, Usb,
  Mouse, Keyboard, Speaker, Watch, Camera, Headphones, Gamepad2,
  Globe, Compass as CompassIcon, Server, Database, CloudCog, Code,
  Terminal, Workflow, Boxes as BoxesIcon, BatteryCharging, Fingerprint,
} from "lucide-react";

/**
 * LoginBackground — small free-flying tech icons.
 *
 * - Icons travel across the viewport (left↔right, top↔bottom, or diagonal).
 * - Each one fades in, glides, and fades out, then loops with a negative-phase delay
 *   so the screen is never empty and never crowded.
 * - No outer ring / circle — pure icons with a soft colored glow.
 * - Subtle SVG constellation lines connect nearby travelers (sampled from current frame).
 */

const ICONS = [
  { I: Rocket,            tint: "#f43f5e" },
  { I: Satellite,         tint: "#06b6d4" },
  { I: Cpu,               tint: "#6366f1" },
  { I: Bot,               tint: "#10b981" },
  { I: CircuitBoard,      tint: "#8b5cf6" },
  { I: Radar,             tint: "#14b8a6" },
  { I: Wifi,              tint: "#0ea5e9" },
  { I: Atom,              tint: "#ef4444" },
  { I: Zap,               tint: "#f59e0b" },
  { I: Cog,               tint: "#64748b" },
  { I: Telescope,         tint: "#7c3aed" },
  { I: Plane,             tint: "#0284c7" },
  { I: Wrench,            tint: "#d97706" },
  { I: Boxes,             tint: "#dc2626" },
  { I: Hexagon,           tint: "#059669" },
  { I: Binary,            tint: "#4f46e5" },
  { I: Network,           tint: "#0891b2" },
  { I: Antenna,           tint: "#a855f7" },
  { I: RadioTower,        tint: "#16a34a" },
  { I: Beaker,            tint: "#ec4899" },
  { I: FlaskConical,      tint: "#0d9488" },
  { I: Activity,          tint: "#ea580c" },
  { I: Waves,             tint: "#2563eb" },
  { I: Sigma,             tint: "#db2777" },
  { I: Orbit,             tint: "#9333ea" },
  { I: Microscope,        tint: "#0369a1" },
  { I: Cable,             tint: "#f97316" },
  { I: Cctv,              tint: "#84cc16" },
  { I: HardDrive,         tint: "#6366f1" },
  { I: Joystick,          tint: "#e11d48" },
  { I: Lightbulb,         tint: "#eab308" },
  { I: MemoryStick,       tint: "#22d3ee" },
  { I: MonitorSmartphone, tint: "#a78bfa" },
  { I: MousePointer,      tint: "#22c55e" },
  { I: Plug,              tint: "#fb923c" },
  { I: Power,             tint: "#0ea5e9" },
  { I: Printer,           tint: "#64748b" },
  { I: ScanLine,          tint: "#f472b6" },
  { I: Smartphone,        tint: "#06b6d4" },
  { I: Tablet,            tint: "#a855f7" },
  { I: Usb,               tint: "#10b981" },
  { I: Mouse,             tint: "#3b82f6" },
  { I: Keyboard,          tint: "#f59e0b" },
  { I: Speaker,           tint: "#8b5cf6" },
  { I: Watch,             tint: "#14b8a6" },
  { I: Camera,            tint: "#ef4444" },
  { I: Headphones,        tint: "#0d9488" },
  { I: Gamepad2,          tint: "#ec4899" },
  { I: Globe,             tint: "#22c55e" },
  { I: CompassIcon,       tint: "#6366f1" },
  { I: Server,            tint: "#0284c7" },
  { I: Database,          tint: "#7c3aed" },
  { I: CloudCog,          tint: "#0891b2" },
  { I: Code,              tint: "#f43f5e" },
  { I: Terminal,          tint: "#16a34a" },
  { I: Workflow,          tint: "#a855f7" },
  { I: BoxesIcon,         tint: "#fb923c" },
  { I: BatteryCharging,   tint: "#22d3ee" },
  { I: Fingerprint,       tint: "#e11d48" },
];

// Deterministic pseudo-random so layout is stable across renders
function seeded(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

// One of 6 travel directions per icon
// 0: left → right, 1: right → left,
// 2: top → bottom, 3: bottom → top,
// 4: TL → BR, 5: BR → TL
function pickPath(rng) {
  return Math.floor(rng() * 6);
}

function generateLayout(count) {
  const rng = seeded(73);
  // Shuffle icon indices so we draw a diverse subset across the available pool
  const pool = ICONS.map((_, i) => i);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const layout = [];
  for (let i = 0; i < count; i++) {
    const icon = ICONS[pool[i % pool.length]];
    const path = pickPath(rng);
    // For a left↔right path, anchor a random Y; for top↔bottom, anchor a random X.
    // For diagonal, use a perpendicular offset (-1 .. +1) to nudge the line.
    const anchor = rng() * 100;                 // 0–100 %
    const size = 14 + Math.floor(rng() * 18);   // 14 – 32 px (small!)
    const dur = 28 + rng() * 32;                // 28 – 60 s per traversal
    const delay = -rng() * dur;                 // start mid-flight, never empty screen
    const rot0 = (rng() - 0.5) * 40;            // gentle base rotation
    const rot1 = rot0 + (rng() - 0.5) * 120;    // spin during travel
    const wobble = 4 + rng() * 10;              // % perpendicular wobble amplitude
    const peak  = 0.55 + rng() * 0.30;          // max opacity during travel
    layout.push({ ...icon, path, anchor, size, dur, delay, rot0, rot1, wobble, peak, idx: i });
  }
  return layout;
}

// Map a path code to (initial CSS variables + keyframe name)
function pathStyle(it) {
  const { path, anchor, wobble, rot0, rot1, peak } = it;
  // Common CSS vars used by keyframes
  const common = {
    ["--rot0"]: `${rot0}deg`,
    ["--rot1"]: `${rot1}deg`,
    ["--peak"]: `${peak}`,
    ["--wob"]: `${wobble}vmin`,
  };
  switch (path) {
    case 0: // left → right
      return { keyframes: "loginFlyLR", style: { ...common, top: `${anchor}%`, left: `-8%` } };
    case 1: // right → left
      return { keyframes: "loginFlyRL", style: { ...common, top: `${anchor}%`, left: `108%` } };
    case 2: // top → bottom
      return { keyframes: "loginFlyTB", style: { ...common, left: `${anchor}%`, top: `-8%` } };
    case 3: // bottom → top
      return { keyframes: "loginFlyBT", style: { ...common, left: `${anchor}%`, top: `108%` } };
    case 4: // top-left → bottom-right (uses diagonal anchor)
      return { keyframes: "loginFlyTLBR", style: { ...common, left: `-8%`, top: `${anchor * 0.6}%` } };
    case 5: // bottom-right → top-left
    default:
      return { keyframes: "loginFlyBRTL", style: { ...common, left: `108%`, top: `${30 + anchor * 0.6}%` } };
  }
}

export default function LoginBackground({ count = 36 }) {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const layout = useMemo(() => generateLayout(count), [count]);

  // rAF-throttled mouse parallax
  useEffect(() => {
    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const w = window.innerWidth || 1;
        const h = window.innerHeight || 1;
        setMouse({
          x: (e.clientX / w - 0.5) * 2,
          y: (e.clientY / h - 0.5) * 2,
        });
        raf = 0;
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
      data-testid="login-background-fx"
    >
      {/* Subtle ambient color wash — adapts to light & dark via CSS variables */}
      <div className="absolute inset-0 bg-[var(--pk-bg)]" />
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-indigo-200/22 dark:bg-indigo-500/10 blur-[180px] login-blob-1" />
      <div className="absolute top-1/4 -right-32 w-[640px] h-[640px] rounded-full bg-cyan-200/20 dark:bg-cyan-500/10 blur-[180px] login-blob-2" />
      <div className="absolute bottom-[-220px] left-1/3 w-[520px] h-[520px] rounded-full bg-amber-200/18 dark:bg-amber-500/8 blur-[170px] login-blob-3" />
      <div className="absolute top-1/2 left-1/2 w-[480px] h-[480px] rounded-full bg-rose-200/15 dark:bg-rose-500/8 blur-[160px] login-blob-4" />

      {/* faint dotted grid */}
      <div
        className="absolute inset-0 opacity-[0.035] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(79,70,229,0.55) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />

      {/* Flying icons — small, free, drifting across the entire screen */}
      {layout.map((it) => {
        const { I, tint, size, dur, delay, idx } = it;
        const ps = pathStyle(it);
        // Parallax — slight, based on icon size (smaller = farther = less movement)
        const depth = size / 32;       // 0.4 – 1.0
        const px = mouse.x * depth * -6;
        const py = mouse.y * depth * -6;
        return (
          <span
            key={idx}
            className="login-bg-icon absolute"
            style={{
              ...ps.style,
              color: tint,
              filter: `drop-shadow(0 0 8px ${tint}88) drop-shadow(0 0 2px ${tint})`,
              animation: `${ps.keyframes} ${dur}s linear ${delay}s infinite`,
              transform: `translate3d(${px}px, ${py}px, 0)`,
              transition: "transform 0.4s cubic-bezier(.2,.7,.2,1)",
              willChange: "transform, top, left, opacity",
            }}
          >
            <I size={size} strokeWidth={2.25} />
          </span>
        );
      })}

      <style>{`
        /* ===== Travel keyframes — each spans the viewport with fade in/out ===== */
        @keyframes loginFlyLR {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(60vw, var(--wob), 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(120vw, 0, 0) rotate(var(--rot1)); opacity: 0; }
        }
        @keyframes loginFlyRL {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(-60vw, calc(var(--wob) * -1), 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(-120vw, 0, 0) rotate(var(--rot1)); opacity: 0; }
        }
        @keyframes loginFlyTB {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(var(--wob), 50vh, 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(0, 110vh, 0) rotate(var(--rot1)); opacity: 0; }
        }
        @keyframes loginFlyBT {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(calc(var(--wob) * -1), -50vh, 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(0, -110vh, 0) rotate(var(--rot1)); opacity: 0; }
        }
        @keyframes loginFlyTLBR {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(55vw, 45vh, 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(120vw, 100vh, 0) rotate(var(--rot1)); opacity: 0; }
        }
        @keyframes loginFlyBRTL {
          0%   { transform: translate3d(0, 0, 0) rotate(var(--rot0)); opacity: 0; }
          10%  { opacity: var(--peak); }
          50%  { transform: translate3d(-55vw, -45vh, 0) rotate(calc(var(--rot0) + (var(--rot1) - var(--rot0)) * 0.5)); }
          90%  { opacity: var(--peak); }
          100% { transform: translate3d(-120vw, -100vh, 0) rotate(var(--rot1)); opacity: 0; }
        }

        /* Slow ambient blobs */
        @keyframes loginBlobDriftA { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(40px, -20px) scale(1.05); } }
        @keyframes loginBlobDriftB { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-30px, 25px) scale(1.08); } }
        @keyframes loginBlobDriftC { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(20px, -30px) scale(1.06); } }
        @keyframes loginBlobDriftD { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-25px, -15px) scale(1.1); } }
        .login-blob-1 { animation: loginBlobDriftA 22s ease-in-out infinite; }
        .login-blob-2 { animation: loginBlobDriftB 26s ease-in-out infinite; }
        .login-blob-3 { animation: loginBlobDriftC 30s ease-in-out infinite; }
        .login-blob-4 { animation: loginBlobDriftD 24s ease-in-out infinite; }

        /* Reduced motion accessibility */
        @media (prefers-reduced-motion: reduce) {
          .login-bg-icon,
          .login-blob-1, .login-blob-2, .login-blob-3, .login-blob-4 {
            animation: none !important;
          }
        }

        /* Mobile — slightly fewer/lighter icons for clarity */
        @media (max-width: 640px) {
          .login-bg-icon { opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}
